import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../utils/auth';
import { useNavigate } from 'react-router-dom';
import { scheduleDay, bookings as bookingsApi, rooms as roomsApi, payments as paymentsApi, pricingPeriods as pricingPeriodsApi, priceProfiles as profilesApi, customers as customersApi, refundRequests as refundRequestsApi, settings as settingsApi } from '../utils/api';
import { format, addDays, subDays, isToday } from 'date-fns';
import { fr } from 'date-fns/locale';
import { ChevronLeft, ChevronRight, Calendar, Plus, Lock, Phone, Unlock, Edit2 } from 'lucide-react';
import toast from 'react-hot-toast';

// ── Helpers ───────────────────────────────────────────────────
function formatPhone(val) {
  const digits = val.replace(/\D/g, '').slice(0, 10);
  return digits.replace(/(\d{2})(?=\d)/g, '$1 ').trim();
}

const CHILD_KEYWORDS = ['enfant', 'child', 'junior'];
const isChild = (name) => CHILD_KEYWORDS.some(k => (name||'').toLowerCase().includes(k));

function calcPricing(profiles, qtys, appliedType) {
  const totalPlayers = Object.values(qtys).reduce((s, q) => s + q, 0);
  const type = appliedType || 'hc'; // défaut HC si non résolu
  let total = 0;
  const breakdown = [];
  const onlyChildren = profiles
    .filter(p => (qtys[p.price_profile_id]||0) > 0)
    .every(p => isChild(p.profile_name));
  for (const p of profiles) {
    const qty = qtys[p.price_profile_id] || 0;
    if (qty <= 0) continue;
    const tiers = (p.tiers||[]).filter(t => Math.max(1,totalPlayers) >= (t.min_players||1));
    const tier  = tiers.length > 0 ? tiers[tiers.length-1] : (p.tiers||[])[0];
    // Utiliser le prix HC ou HP selon la période active
    const priceFlat = type === 'hp' ? tier?.priceFlatHp       : tier?.priceFlatHc;
    const pricePerP = type === 'hp' ? tier?.pricePerPersonHp   : tier?.pricePerPersonHc;
    const lineTotal = priceFlat
      ? parseFloat(priceFlat)
      : (parseFloat(pricePerP||0) * qty);
    total += lineTotal;
    breakdown.push({ profileId: p.price_profile_id, profileName: p.profile_name, qty, lineTotal });
  }
  return { total: total.toFixed(2), totalPlayers, breakdown, onlyChildren };
}

// ── Sélecteur joueurs admin ───────────────────────────────────
function AdminPlayerPicker({ prices, appliedType, onChange }) {
  const profiles = (prices || []).filter(Boolean);
  const [qtys, setQtys] = useState({});

  useEffect(() => {
    if (profiles.length > 0) {
      const init = {};
      for (const p of profiles) init[p.price_profile_id] = 0;
      setQtys(init);
      onChange(calcPricing(profiles, init, appliedType));
    }
  // eslint-disable-next-line
  }, [profiles.map(p=>p.price_profile_id).join(',')]);

  const upd = (profileId, delta) => {
    setQtys(prev => {
      const next = { ...prev, [profileId]: Math.max(0, (prev[profileId]||0) + delta) };
      onChange(calcPricing(profiles, next, appliedType));
      return next;
    });
  };

  const totalPlayers = Object.values(qtys).reduce((s,q) => s+q, 0);

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
      {profiles.map(p => {
        const qty = qtys[p.price_profile_id] || 0;
        const tiers = (p.tiers||[]).filter(t => Math.max(1,totalPlayers) >= (t.min_players||1));
        const tier  = tiers.length > 0 ? tiers[tiers.length-1] : (p.tiers||[])[0];
        const priceFlat = appliedType==='hp' ? (tier?.priceFlatHp||tier?.priceFlatHc) : (tier?.priceFlatHc||tier?.priceFlatHp);
        const pricePerP = appliedType==='hp' ? (tier?.pricePerPersonHp||tier?.pricePerPersonHc) : (tier?.pricePerPersonHc||tier?.pricePerPersonHp);
        const unitPrice = priceFlat ? null : parseFloat(pricePerP||0);
        const lineTotal = priceFlat ? parseFloat(priceFlat) : unitPrice * qty;
        return (
          <div key={p.price_profile_id} style={{
            display:'flex', alignItems:'center', gap:10, padding:'10px 12px', borderRadius:8,
            background: qty > 0 ? 'rgba(124,92,252,0.08)' : 'var(--bg-elevated)',
            border:`1px solid ${qty > 0 ? 'rgba(124,92,252,0.2)' : 'var(--border)'}`,
          }}>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:13, fontWeight:600 }}>{p.profile_name}</div>
              <div style={{ fontSize:11, color:'var(--text-3)' }}>
                {priceFlat ? `${parseFloat(priceFlat).toFixed(0)} € fixe` : unitPrice ? `${unitPrice.toFixed(0)} €/pers` : '—'}
              </div>
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:6 }}>
              <button type="button" onClick={() => upd(p.price_profile_id, -1)} disabled={qty<=0}
                style={{ width:28, height:28, borderRadius:6, border:'1px solid var(--border)',
                  background:'var(--bg-base)', color:qty>0?'var(--text-1)':'var(--text-3)',
                  cursor:qty>0?'pointer':'not-allowed', fontSize:16,
                  display:'flex', alignItems:'center', justifyContent:'center' }}>−</button>
              <input type="number" min={0} value={qty}
                onChange={e => {
                  const v = Math.max(0, parseInt(e.target.value)||0);
                  setQtys(prev => {
                    const next = {...prev, [p.price_profile_id]: v};
                    onChange(calcPricing(profiles, next, appliedType));
                    return next;
                  });
                }}
                style={{ width:36, textAlign:'center', background:'var(--bg-base)',
                  border:'1px solid var(--border)', borderRadius:6, color:'var(--text-1)',
                  fontSize:14, fontWeight:700, padding:'3px 0', fontFamily:'monospace' }}/>
              <button type="button" onClick={() => upd(p.price_profile_id, 1)}
                style={{ width:28, height:28, borderRadius:6, border:'1px solid var(--border)',
                  background:'var(--bg-base)', color:'var(--text-1)',
                  cursor:'pointer', fontSize:16,
                  display:'flex', alignItems:'center', justifyContent:'center' }}>+</button>
            </div>
            <span style={{ minWidth:50, textAlign:'right', fontWeight:700, fontSize:13,
              color: qty > 0 ? 'var(--teal)' : 'var(--text-3)' }}>
              {qty > 0 ? `${lineTotal.toFixed(0)} €` : '—'}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// Couleurs statut
const STATUS_STYLE = {
  available:  { bg:'rgba(0,210,180,0.08)', border:'rgba(0,210,180,0.2)',  color:'var(--teal)',         label:'Libre' },
  booked:     { bg:'rgba(124,92,252,0.15)', border:'rgba(124,92,252,0.4)', color:'var(--accent-light)', label:'Réservé' },
  blocked:    { bg:'rgba(255,77,109,0.08)', border:'rgba(255,77,109,0.2)', color:'var(--red)',           label:'Bloqué' },
  call_only:  { bg:'rgba(245,166,35,0.08)', border:'rgba(245,166,35,0.2)', color:'var(--amber)',         label:'Sur appel' },
};


// ── Modale création réservation ───────────────────────────────
function NewBookingModal({ slot, room, date: initialDate, onClose, onSaved, onNavigate }) {
  const [allRooms, setAllRooms]   = useState([]);
  const [selectedRoom, setSelRoom] = useState(room || null);
  const [selectedDate, setSelDate] = useState(initialDate || '');
  const [selectedTime, setSelTime] = useState(slot?.time || '');
  const [appliedType,  setAppliedType]  = useState('hc');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [form, setForm] = useState({
    customerName: '', customerEmail: '', customerPhone: '',
    playerCount: room?.min_players || 2,
    totalAmount: '', internalNotes: '',
    priceProfileId: '',
    customerType: 'individual',
    companyName: '', companySiret: '', companyVat: '', companyAddress: '',
  });
  const [prices, setPrices] = useState([]);
  const [saving, setSaving] = useState(false);
  const breakdownRef = useRef(null); // ref pour éviter les pb async avec setState
  const [customerSearch, setCustSearch] = useState('');
  const [customerResults, setCustResults] = useState([]);
  const [selectedCustomer, setSelCustomer] = useState(null);
  const [custLoading, setCustLoading] = useState(false);

  // Recherche client
  const searchCustomer = async (q) => {
    setCustSearch(q);
    if (q.length < 2) { setCustResults([]); return; }
    setCustLoading(true);
    try {
      const r = await customersApi.search(q);
      setCustResults(r.data.customers || []);
    } catch {} finally { setCustLoading(false); }
  };

  const pickCustomer = (customer) => {
    setSelCustomer(customer);
    setForm(f => ({
      ...f,
      customerName: `${customer.first_name} ${customer.last_name}`.trim(),
      customerEmail: customer.email || f.customerEmail,
      customerPhone: customer.phone || f.customerPhone,
    }));
    setCustSearch('');
    setCustResults([]);
  };

  // Charger les salles si pas de salle pré-sélectionnée
  useEffect(() => {
    if (!room) {
      roomsApi.list().then(r => setAllRooms(r.data.rooms.filter(r => r.is_active)));
    }
  }, [room]);

  // Charger les créneaux quand salle + date sélectionnés
  useEffect(() => {
    if (selectedRoom && selectedDate) {
      const dow = new Date(selectedDate + 'T12:00:00').getDay();
      const slots = (selectedRoom.time_slots || [])
        .filter(s => !s.day_of_week?.length || s.day_of_week.includes(dow))
        .map(s => s.start_time.slice(0,5))
        .sort();
      setAvailableSlots(slots);
      if (slots.length > 0 && !selectedTime) setSelTime(slots[0]);
    }
  }, [selectedRoom?.id, selectedDate]);

  useEffect(() => {
    if (!selectedRoom?.id || !selectedDate || !selectedTime) return;
    pricingPeriodsApi.resolve({
      roomId: selectedRoom.id,
      date:   selectedDate,
      time:   selectedTime,
    }).then(r => setAppliedType(r.data.appliedType || 'hc'))
      .catch(() => setAppliedType('hc'));
  }, [selectedRoom?.id, selectedDate, selectedTime]);

  useEffect(() => {
    if (selectedRoom) {
      roomsApi.get(selectedRoom.id).then(r => {
        const ps = (r.data.room?.prices || []).filter(Boolean);
        // Trier par prix décroissant
        ps.sort((a, b) => {
          const priceA = (a.tiers?.[0]?.priceFlatHp || a.tiers?.[0]?.priceFlatHc) ?? ((a.tiers?.[0]?.pricePerPersonHp || a.tiers?.[0]?.pricePerPersonHc) * 2) ?? 0;
          const priceB = (b.tiers?.[0]?.priceFlatHp || b.tiers?.[0]?.priceFlatHc) ?? ((b.tiers?.[0]?.pricePerPersonHp || b.tiers?.[0]?.pricePerPersonHc) * 2) ?? 0;
          return priceB - priceA;
        });
        setPrices(ps);
        if (ps[0]) setForm(f => ({ ...f, priceProfileId: ps[0].price_profile_id }));
      }).catch(() => {});
    }
  }, [selectedRoom?.id]);

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.customerName.trim()) return toast.error('Nom requis');
    if (!selectedRoom) return toast.error('Salle requise');
    if (!selectedDate) return toast.error('Date requise');
    if (!selectedTime) return toast.error('Créneau requis');
    setSaving(true);
    try {
      const r = await bookingsApi.create({
        roomId: selectedRoom.id,
        sessionDate: selectedDate,
        sessionStart: selectedTime,
        source: 'admin',
        customerId: selectedCustomer?.id || null,
        customerName: form.customerName,
        customerEmail: form.customerEmail,
        customerPhone: form.customerPhone,
        playerCount: parseInt(form.playerCount) || 1,
        totalAmount: parseFloat(form.totalAmount) || 0,
        internalNotes: form.internalNotes,
        priceProfileId: form.priceProfileId || null,
        priceBreakdown: breakdownRef.current ? JSON.stringify(breakdownRef.current) : null,
        customerType:    form.customerType,
        companyName:     form.customerType === 'company' ? form.companyName    : null,
        companySiret:    form.customerType === 'company' ? form.companySiret   : null,
        companyVat:      form.customerType === 'company' ? form.companyVat     : null,
        companyAddress:  form.customerType === 'company' ? form.companyAddress : null,
      });
      toast.success('Réservation créée');
      onSaved();
      onClose();
      // Ouvrir directement la fiche réservation
      const bookingId = r.data?.booking?.id || r.data?.id;
      if (bookingId) onNavigate(bookingId);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erreur');
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target===e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth:520 }}>
        <h3 className="modal-title">
          Nouvelle réservation{selectedRoom ? ` — ${selectedRoom.name}` : ''}
        </h3>
        {/* Sélection salle si pas pré-sélectionnée */}
        {!room && (
          <div className="form-group">
            <label className="form-label">Salle *</label>
            <select className="input" value={selectedRoom?.id||''}
              onChange={e => {
                const r = allRooms.find(r => r.id === e.target.value);
                setSelRoom(r||null); setSelTime('');
              }}>
              <option value="">— Choisir une salle —</option>
              {allRooms.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </div>
        )}

        {/* Sélection date + créneau si pas pré-sélectionnés */}
        {(!slot || !initialDate) && (
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Date *</label>
              <input type="date" className="input" value={selectedDate}
                onChange={e => { setSelDate(e.target.value); setSelTime(''); }}/>
            </div>
            <div className="form-group">
              <label className="form-label">Créneau *</label>
              {availableSlots.length > 0 ? (
                <select className="input" value={selectedTime} onChange={e => setSelTime(e.target.value)}>
                  <option value="">— Choisir —</option>
                  {availableSlots.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              ) : (
                <input type="time" className="input" value={selectedTime}
                  onChange={e => setSelTime(e.target.value)}/>
              )}
            </div>
          </div>
        )}

        {/* Récap si tout est sélectionné */}
        {selectedRoom && selectedDate && selectedTime && (
          <div style={{ padding:'10px 14px', background:'var(--bg-elevated)', borderRadius:8, marginBottom:12, fontSize:13 }}>
            📅 {format(new Date(selectedDate+'T12:00'), 'EEEE d MMMM yyyy', { locale:fr })} à <strong>{selectedTime}</strong>
            {!room && <span style={{marginLeft:8, color:'var(--accent-light)'}}>· {selectedRoom.name}</span>}
          </div>
        )}

        {/* Recherche client existant */}
        <div className="form-group" style={{position:'relative'}}>
          <label className="form-label">Rechercher un client existant</label>
          <input className="input" value={customerSearch}
            onChange={e => searchCustomer(e.target.value)}
            placeholder="Nom, prénom, email ou téléphone…"/>
          {custLoading && <span className="spinner" style={{width:14,height:14,position:'absolute',right:12,top:34}}/>}
          {selectedCustomer && (
            <div style={{marginTop:6,padding:'6px 10px',background:'rgba(0,210,180,0.1)',
              border:'1px solid var(--teal)',borderRadius:8,fontSize:12,color:'var(--teal)',
              display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              ✓ {selectedCustomer.first_name} {selectedCustomer.last_name}
              <button type="button" onClick={()=>{setSelCustomer(null);setCustSearch('');}}
                style={{background:'none',border:'none',cursor:'pointer',color:'var(--text-3)'}}>×</button>
            </div>
          )}
          {customerResults.length > 0 && (
            <div style={{position:'absolute',top:'100%',left:0,right:0,zIndex:50,
              background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:8,
              boxShadow:'0 8px 24px rgba(0,0,0,0.4)',maxHeight:200,overflowY:'auto'}}>
              {customerResults.map(cu => (
                <div key={cu.id} onClick={() => pickCustomer(cu)}
                  style={{padding:'10px 14px',cursor:'pointer',borderBottom:'1px solid var(--border)',fontSize:13}}
                  onMouseEnter={e=>e.currentTarget.style.background='var(--bg-elevated)'}
                  onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                  <div style={{fontWeight:600}}>{cu.first_name} {cu.last_name}</div>
                  <div style={{fontSize:11,color:'var(--text-3)'}}>{[cu.email,cu.phone].filter(Boolean).join(' · ')}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Nom client *</label>
            <input className="input" value={form.customerName} onChange={e => upd('customerName', e.target.value)} placeholder="Jean Dupont"/>
          </div>
          <div className="form-group">
            <label className="form-label">Téléphone</label>
            <input className="input" value={form.customerPhone} onChange={e => upd('customerPhone', formatPhone(e.target.value))} placeholder="06 12 34 56 78"/>
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Email</label>
          <input type="email" className="input" value={form.customerEmail} onChange={e => upd('customerEmail', e.target.value)} placeholder="jean@exemple.fr"/>
        </div>

        {/* Toggle particulier / entreprise */}
        <div className="form-group">
          <label className="form-label">Type de client</label>
          <div style={{ display:'flex', gap:8 }}>
            {[['individual','👤 Particulier'],['company','🏢 Entreprise']].map(([val, label]) => (
              <button key={val} type="button"
                onClick={() => upd('customerType', val)}
                style={{ flex:1, padding:'9px', borderRadius:8, cursor:'pointer',
                  fontFamily:'inherit', fontSize:13, fontWeight:600,
                  border:`1.5px solid ${form.customerType===val ? 'var(--accent)' : 'var(--border)'}`,
                  background: form.customerType===val ? 'rgba(124,92,252,0.1)' : 'var(--bg-elevated)',
                  color: form.customerType===val ? 'var(--accent-light)' : 'var(--text-2)',
                  transition:'all 0.12s' }}>
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Champs entreprise */}
        {form.customerType === 'company' && (
          <>
            <div className="form-group">
              <label className="form-label">Raison sociale *</label>
              <input className="input" value={form.companyName}
                onChange={e => upd('companyName', e.target.value)}
                placeholder="SARL Mon Entreprise"/>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">SIRET</label>
                <input className="input" value={form.companySiret}
                  onChange={e => upd('companySiret', e.target.value)}
                  placeholder="12345678901234" maxLength={14}/>
              </div>
              <div className="form-group">
                <label className="form-label">N° TVA intracommunautaire</label>
                <input className="input" value={form.companyVat}
                  onChange={e => upd('companyVat', e.target.value)}
                  placeholder="FR12345678901"/>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Adresse de facturation</label>
              <input className="input" value={form.companyAddress}
                onChange={e => upd('companyAddress', e.target.value)}
                placeholder="1 rue de la Paix, 75001 Paris"/>
            </div>
          </>
        )}

        {prices.length > 0 && (
          <div className="form-group">
            <label className="form-label" style={{marginBottom:8}}>Composition du groupe</label>
            <AdminPlayerPicker prices={prices} appliedType={appliedType} onChange={pricing => {
              upd('playerCount', pricing.totalPlayers);
              upd('totalAmount', pricing.total);
              breakdownRef.current = pricing.breakdown;
            }}/>
          </div>
        )}
        {prices.length === 0 && (
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Nb joueurs</label>
              <input type="number" className="input" min={0} max={selectedRoom?.max_players||20}
                value={form.playerCount} onChange={e => upd('playerCount', e.target.value)}/>
            </div>
            <div className="form-group">
              <label className="form-label">Montant total (€)</label>
              <input type="number" step="0.01" className="input" value={form.totalAmount}
                onChange={e => upd('totalAmount', e.target.value)} placeholder="0.00"/>
            </div>
          </div>
        )}
        <div className="form-group">
          <label className="form-label">Notes internes</label>
          <input className="input" value={form.internalNotes} onChange={e => upd('internalNotes', e.target.value)} placeholder="Remarques…"/>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Annuler</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? <span className="spinner" style={{width:16,height:16}}/> : 'Créer la réservation'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Modale override créneau ───────────────────────────────────
function OverrideModal({ slot, room, date, onClose, onSaved }) {
  const [status, setStatus] = useState(slot.override?.status || 'available');
  const [reason, setReason] = useState(slot.override?.reason || '');
  const [newTime, setNewTime] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      if (newTime && newTime !== slot.time) {
        await scheduleDay.setTime({ roomId: room.id, date, oldTime: slot.time, newTime });
        toast.success('Horaire modifié');
      } else {
        await scheduleDay.setOverride({ roomId: room.id, date, startTime: slot.time, status, reason });
        toast.success('Créneau mis à jour');
      }
      onSaved();
      onClose();
    } catch(e) { toast.error(e.response?.data?.error || 'Erreur'); }
    finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target===e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth:420 }}>
        <h3 className="modal-title">
          {room.name} — {slot.time} · {format(new Date(date), 'd MMM', { locale:fr })}
        </h3>

        <div className="form-group">
          <label className="form-label">Statut du créneau</label>
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {[
              ['available', <Unlock size={14}/>, 'Ouvert', 'Réservable normalement'],
              ['call_only', <Phone size={14}/>, 'Sur appel', 'Le client doit appeler pour réserver'],
              ['blocked',   <Lock size={14}/>,  'Bloqué',   'Créneau fermé / indisponible'],
            ].map(([val, icon, label, desc]) => (
              <div key={val} onClick={() => setStatus(val)} style={{
                display:'flex', alignItems:'center', gap:12, padding:'12px 14px',
                borderRadius:10, cursor:'pointer',
                border:`1px solid ${status===val ? STATUS_STYLE[val].border : 'var(--border)'}`,
                background: status===val ? STATUS_STYLE[val].bg : 'transparent',
              }}>
                <div style={{ color: STATUS_STYLE[val].color }}>{icon}</div>
                <div>
                  <div style={{ fontWeight:600, fontSize:14 }}>{label}</div>
                  <div style={{ fontSize:12, color:'var(--text-3)' }}>{desc}</div>
                </div>
                {status===val && <div style={{ marginLeft:'auto', width:8, height:8, borderRadius:'50%', background: STATUS_STYLE[val].color }}/>}
              </div>
            ))}
          </div>
        </div>

        {status !== 'available' && (
          <div className="form-group">
            <label className="form-label">Raison (optionnel)</label>
            <input className="input" value={reason} onChange={e => setReason(e.target.value)}
              placeholder="Ex: Privatisation, Maintenance…"/>
          </div>
        )}

        <div className="form-group" style={{marginTop:8, paddingTop:12, borderTop:'1px solid var(--border)'}}>
          <label className="form-label">Modifier l'horaire (optionnel)</label>
          <div style={{display:'flex', alignItems:'center', gap:8}}>
            <span className="text-sm text-muted">Déplacer {slot.time} →</span>
            <input type="time" className="input" style={{width:130}} value={newTime}
              onChange={e => setNewTime(e.target.value)}
              placeholder="Nouvel horaire"/>
          </div>
          {newTime && newTime !== slot.time && (
            <p style={{fontSize:11, color:'var(--amber)', marginTop:4}}>
              ⚠ L'ancien créneau sera bloqué, le nouveau sera ouvert
            </p>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Annuler</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? <span className="spinner" style={{width:16,height:16}}/> : 'Sauvegarder'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Cellule créneau ───────────────────────────────────────────
function SlotCell({ slot, room, date, onEdit, onBook, onViewBooking, isPastSlot }) {
  const st = STATUS_STYLE[slot.status] || STATUS_STYLE.available;
  const b  = slot.booking;
  // Créneau passé (marge de sécurité incluse) et non réservé : on le grise
  // pour signaler visuellement qu'il n'est plus exploitable, sans le
  // masquer (l'historique du planning reste visible et éditable).
  const greyed = isPastSlot && !b;

  return (
    <div style={{
      background: greyed ? 'rgba(255,255,255,0.03)' : st.bg,
      border: `1px solid ${greyed ? 'var(--border)' : st.border}`,
      borderRadius: 8,
      padding: '8px 10px',
      marginBottom: 4,
      cursor: 'pointer',
      transition: 'all 0.15s',
      position: 'relative',
      opacity: greyed ? 0.5 : 1,
    }}
    onClick={() => slot.status === 'booked' ? onViewBooking(slot.booking) : onEdit(slot, room)}
    onMouseEnter={e => e.currentTarget.style.opacity = greyed ? '0.7' : '0.85'}
    onMouseLeave={e => e.currentTarget.style.opacity = greyed ? '0.5' : '1'}>

      {/* Ligne 1 : heure + ref (ou icône statut) */}
      <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom: b ? 3 : 0 }}>
        <span style={{ fontFamily:'monospace', fontWeight:700, fontSize:13, color: greyed ? 'var(--text-3)' : st.color }}>
          {slot.time}
        </span>
        {slot.status === 'blocked'   && <Lock size={11} color="var(--red)"/>}
        {slot.status === 'call_only' && <Phone size={11} color="var(--amber)"/>}
        {slot.override?.reason && (
          <span style={{ fontSize:10, color:'var(--text-3)', fontStyle:'italic' }}>
            {slot.override.reason}
          </span>
        )}
        {b && (
          <span style={{ marginLeft:'auto', fontSize:10, fontFamily:'monospace',
            color:'var(--accent-light)', flexShrink:0 }}>
            {b.booking_ref}
          </span>
        )}
        {slot.status === 'available' && !b && !greyed && (
          <button onClick={e => { e.stopPropagation(); onBook(slot, room); }}
            style={{ marginLeft:'auto', background:'none', border:'none', cursor:'pointer',
              padding:2, color:'var(--teal)', borderRadius:4 }}
            title="Créer une réservation">
            <Plus size={11}/>
          </button>
        )}
      </div>

      {b && (
        <div>
          {/* Badge pending */}
          {b.booking_status === 'pending' && (
            <div style={{ fontSize:10, fontWeight:700, color:'var(--amber)',
              background:'rgba(245,166,35,0.1)', borderRadius:4, padding:'1px 6px',
              marginBottom:4, display:'inline-block' }}>
              ⏳ En attente paiement
            </div>
          )}
          {/* Nom — anonyme si pending */}
          <div style={{ fontSize:12, fontWeight:600, color: b.booking_status==='pending' ? 'var(--text-3)' : 'var(--text-1)', marginBottom:2,
            lineHeight:1.3, fontStyle: b.booking_status==='pending' ? 'italic' : 'normal' }}>
            {b.booking_status === 'pending' ? 'Client en cours…' : b.customer_name}
          </div>
          <div style={{ fontSize:11, color:'var(--text-3)', display:'flex', gap:6, flexWrap:'wrap' }}>
            <span>{b.player_count} joueur{b.player_count>1?'s':''}</span>
            {b.customer_phone && <span>· {b.customer_phone}</span>}
            {b.gm_first && <span>· {b.gm_first} {b.gm_last?.[0]}.</span>}
            <span style={{ marginLeft:'auto', flexShrink:0, color:
              b.payment_status==='paid' ? 'var(--teal)' :
              b.payment_status==='partial' ? 'var(--amber)' : 'var(--red)' }}>
              {b.payment_status==='paid' ? '✓ Soldé' : b.payment_status==='partial' ? '~ Acompte' : '✗ Impayé'}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Page principale ───────────────────────────────────────────
export default function BookingsPage() {
  const { can } = useAuth();
  const navigate  = useNavigate();
  const [date, setDate]     = useState(format(new Date(), 'yyyy-MM-dd'));
  const [rooms, setRooms]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [editSlot, setEditSlot] = useState(null);
  const [bookSlot, setBookSlot] = useState(null);
  const [filterSite, setFilterSite] = useState('');
  const [sites, setSites]   = useState([]);
  const [showCancelled, setShowCancelled] = useState(false);
  const [cancelledBookings, setCancelledBookings] = useState([]);
  const [cancelledLoading, setCancelledLoading] = useState(false);
  // Marge de sécurité (en minutes) utilisée pour griser les créneaux passés :
  // même réglage que le délai d'appel obligatoire côté réservation publique
  // (booking_cutoff_min), pour rester cohérent avec ce qui est réellement
  // encore réservable.
  const [cutoffMin, setCutoffMin] = useState(30);

  useEffect(() => {
    settingsApi.get().then(r => {
      const arr = r.data.settings || [];
      const get = (key) => (Array.isArray(arr) ? arr.find(s=>s.key===key)?.value : arr[key]) || '';
      const v = parseInt(get('booking_cutoff_min'));
      if (!isNaN(v)) setCutoffMin(v);
    }).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await scheduleDay.get(date);
      setRooms(r.data.rooms);
      // Extraire les sites uniques
      const s = [...new Map(r.data.rooms
        .filter(r => r.site_id)
        .map(r => [r.site_id, { id:r.site_id, name:r.site_name, color:r.site_color }])
      ).values()];
      setSites(s);
    } catch { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  }, [date]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!showCancelled) return;
    setCancelledLoading(true);
    bookingsApi.list({ status: 'cancelled' })
      .then(r => setCancelledBookings(r.data.bookings || []))
      .catch(() => toast.error('Erreur chargement annulées'))
      .finally(() => setCancelledLoading(false));
  }, [showCancelled, date]);

  const displayed = (filterSite ? rooms.filter(r => r.site_id === filterSite) : rooms)
    .slice()
    .sort((a, b) => {
      // Trier par premier créneau du jour, puis par nom
      const aFirst = a.slots?.[0]?.time || '99:99';
      const bFirst = b.slots?.[0]?.time || '99:99';
      if (aFirst !== bFirst) return aFirst.localeCompare(bFirst);
      return (a.name || '').localeCompare(b.name || '');
    });

  const handleBook = (slot, room) => { setBookSlot({ slot, room }); };

  // Calculer toutes les heures uniques pour l'axe vertical
  const allTimes = [...new Set(
    rooms.flatMap(r => r.slots.map(s => s.time))
  )].sort();

  // Un créneau est considéré "passé" si : le jour affiché est avant
  // aujourd'hui (journée entièrement révolue), ou si on est aujourd'hui et
  // que l'heure du créneau, moins la marge de sécurité, est déjà dépassée.
  const todayStr = format(new Date(), 'yyyy-MM-dd');
  const now = new Date();
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  const isSlotPast = (time) => {
    if (date < todayStr) return true;
    if (date > todayStr) return false;
    const [h, m] = time.split(':').map(Number);
    return (h * 60 + m) - cutoffMin <= nowMinutes;
  };

  return (
    <>
      <div className="page-header">
        <div>
          <h2>Planning des créneaux</h2>
          <p>Vue jour — gestion des réservations et statuts</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className={`btn btn-sm ${!showCancelled ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setShowCancelled(false)}>Planning</button>
          <button className={`btn btn-sm ${showCancelled ? 'btn-danger' : 'btn-secondary'}`}
            onClick={() => setShowCancelled(true)}>Annulées</button>
          {can('bookings.create') && !showCancelled && (
            <button className="btn btn-primary" onClick={() => setBookSlot({ slot: null, room: null })}>
              <Plus size={15}/> Nouvelle réservation
            </button>
          )}
        </div>
      </div>

      <div className="page-body">
        {/* Navigation date + filtres */}
        <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20, flexWrap:'wrap' }}>
          <button className="btn btn-ghost btn-sm" onClick={() => setDate(format(subDays(new Date(date),1),'yyyy-MM-dd'))}>
            <ChevronLeft size={16}/>
          </button>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <Calendar size={16} color="var(--text-3)"/>
            <span style={{ fontWeight:700, fontSize:15, minWidth:180 }}>
              {format(new Date(date+'T12:00'), 'EEEE d MMMM yyyy', { locale:fr })}
              {isToday(new Date(date+'T12:00')) && (
                <span className="badge badge-green" style={{ marginLeft:8, fontSize:10 }}>Aujourd'hui</span>
              )}
            </span>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={() => setDate(format(addDays(new Date(date),1),'yyyy-MM-dd'))}>
            <ChevronRight size={16}/>
          </button>
          <input type="date" className="input" style={{ width:160 }}
            value={date} onChange={e => setDate(e.target.value)}/>

          {/* Filtre site */}
          {sites.length > 1 && (
            <div style={{ display:'flex', gap:6, marginLeft:8 }}>
              <button onClick={() => setFilterSite('')}
                className={`btn btn-sm ${!filterSite ? 'btn-primary' : 'btn-secondary'}`}>
                Tous
              </button>
              {sites.map(s => (
                <button key={s.id} onClick={() => setFilterSite(s.id)}
                  className={`btn btn-sm ${filterSite===s.id ? 'btn-primary' : 'btn-secondary'}`}
                  style={{ borderColor: filterSite===s.id ? s.color : undefined }}>
                  {s.name}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Légende */}
        <div style={{ display:'flex', gap:12, marginBottom:16, flexWrap:'wrap' }}>
          {Object.entries(STATUS_STYLE).map(([k, v]) => (
            <div key={k} style={{ display:'flex', alignItems:'center', gap:5, fontSize:12 }}>
              <div style={{ width:10, height:10, borderRadius:2, background:v.bg, border:`1px solid ${v.border}` }}/>
              <span style={{ color:'var(--text-2)' }}>{v.label}</span>
            </div>
          ))}
        </div>

        {loading ? (
          <div className="empty-state"><span className="spinner"/></div>
        ) : displayed.length === 0 ? (
          <div className="empty-state card"><p>Aucune salle configurée</p></div>
        ) : (
          /* Grille : 1 colonne par salle */
          <div style={{ overflowX:'auto' }}>
            <div style={{
              display:'grid',
              gridTemplateColumns:`repeat(${displayed.length}, minmax(180px, 1fr))`,
              gap:12, minWidth: displayed.length * 180,
            }}>
              {displayed.map(room => (
                <div key={room.id}>
                  {/* En-tête salle */}
                  <div style={{
                    padding:'10px 12px', marginBottom:8, borderRadius:8,
                    background:'var(--bg-elevated)', borderLeft:`3px solid ${room.site_color||'var(--accent)'}`,
                  }}>
                    <div style={{ fontWeight:700, fontSize:13 }}>{room.name}</div>
                    {room.site_name && (
                      <div style={{ fontSize:11, color:'var(--text-3)' }}>{room.site_name}</div>
                    )}
                    <div style={{ fontSize:11, color:'var(--text-3)', marginTop:2 }}>
                      {room.slots.length} créneaux · {room.duration_min} min
                    </div>
                  </div>

                  {/* Créneaux */}
                  {room.slots.length === 0 ? (
                    <div style={{ fontSize:12, color:'var(--text-3)', textAlign:'center', padding:16 }}>
                      Pas de créneau ce jour
                    </div>
                  ) : (
                    room.slots.map(slot => (
                      <SlotCell key={slot.time} slot={slot} room={room} date={date}
                        onEdit={can('bookings.edit') ? (s, r) => setEditSlot({slot:s, room:r}) : null}
                        onBook={can('bookings.create') ? handleBook : null}
                        onViewBooking={(b) => navigate('/app/reservations/'+b.id)}
                        isPastSlot={isSlotPast(slot.time)}
                      />
                    ))
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── Vue annulées ── */}
      {showCancelled && (
        <div className="page-body">
          {cancelledLoading ? (
            <div className="empty-state"><span className="spinner"/></div>
          ) : cancelledBookings.length === 0 ? (
            <div className="empty-state card"><p>Aucune réservation annulée</p></div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Réf.</th>
                    <th>Client</th>
                    <th>Salle</th>
                    <th>Session</th>
                    <th className="text-right">Montant</th>
                    <th>Avoir / Remboursement</th>
                  </tr>
                </thead>
                <tbody>
                  {cancelledBookings.map(b => (
                    <tr key={b.id} style={{ cursor: 'pointer' }}
                      onClick={() => navigate('/app/reservations/' + b.id)}>
                      <td><span style={{ fontFamily: 'monospace', fontWeight: 700 }}>{b.booking_ref}</span></td>
                      <td>{b.customer_name}</td>
                      <td style={{ fontSize: 13 }}>{b.room_name}</td>
                      <td style={{ fontSize: 13, color: 'var(--text-2)' }}>
                        {b.session_date
                          ? new Date(b.session_date + 'T12:00').toLocaleDateString('fr-FR', { day:'numeric', month:'short', year:'numeric' })
                          : '—'}
                      </td>
                      <td className="text-right" style={{ fontWeight: 700 }}>
                        {parseFloat(b.total_amount || 0).toFixed(2)} €
                      </td>
                      <td>
                        {b.refund_status === 'pending' && (
                          <span style={{ display:'inline-flex', alignItems:'center', gap:5,
                            padding:'3px 10px', borderRadius:99, fontSize:12, fontWeight:600,
                            background:'rgba(245,166,35,0.1)', color:'var(--amber)' }}>
                            ⏳ Remboursement en attente
                          </span>
                        )}
                        {b.refund_status === 'approved' && (
                          <span style={{ display:'inline-flex', alignItems:'center', gap:5,
                            padding:'3px 10px', borderRadius:99, fontSize:12, fontWeight:600,
                            background:'rgba(0,210,180,0.1)', color:'var(--teal)' }}>
                            ✓ Remboursé
                          </span>
                        )}
                        {b.avoir_code && (
                          <span style={{ display:'inline-flex', alignItems:'center', gap:5,
                            padding:'3px 10px', borderRadius:99, fontSize:12, fontWeight:600,
                            background:'rgba(124,92,252,0.1)', color:'var(--accent-light)',
                            fontFamily:'monospace' }}>
                            🎁 Avoir {b.avoir_code}
                          </span>
                        )}
                        {!b.refund_status && !b.avoir_code && (
                          <span style={{ fontSize:12, color:'var(--text-3)' }}>—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Modale nouvelle réservation */}
      {bookSlot && (
        <NewBookingModal
          slot={bookSlot.slot} room={bookSlot.room} date={date}
          onClose={() => setBookSlot(null)}
          onSaved={load}
          onNavigate={(id) => { setBookSlot(null); navigate('/app/reservations/'+id); }}
        />
      )}

      {/* Modale override */}
      {editSlot && (
        <OverrideModal
          slot={editSlot.slot} room={editSlot.room} date={date}
          onClose={() => setEditSlot(null)}
          onSaved={load}
        />
      )}
    </>
  );
}
